import { AlertCircle, AlertTriangle, Info, MessageCircle, RotateCcw, Send, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AssetImage } from "./AssetImage";
import { createAssistantPromptState, reduceAssistantPromptState } from "./assistantPromptState";
import type { AssistantPromptKind, AssistantTaskEvent } from "./assistantPromptState";
import { AiRequestAbortedError, createLocalAiGuidance, requestAiGuidance } from "../lib/ai";
import { parseBoldMarkdown } from "../lib/boldMarkdown";
import { MAX_AI_THREAD_MESSAGES } from "../types";
import type { AiDialogueMessage, AiMessage, AiResponse, AiThreadId, AssistantContextSnapshot, ChapterId, EvidenceId, FallbackReason, SceneId } from "../types";

interface AiCompanionProps {
  open: boolean;
  threadId: AiThreadId;
  chapterId: ChapterId | "quiz";
  snapshot: AssistantContextSnapshot;
  aiMode: "online" | "local";
  /** @deprecated 密钥由服务端管理，保留仅兼容旧调用方。 */
  apiKey?: string;
  messages: readonly AiMessage[];
  introAcknowledged: boolean;
  taskEvent: AssistantTaskEvent | null;
  onHintUsed: () => void;
  onIntroAcknowledged: () => void;
  onEvidence: (paragraphId: EvidenceId, title?: string) => void;
  onEvidenceViewed: (evidenceId: EvidenceId, sceneId: SceneId) => void;
  onThreadChange: (threadId: AiThreadId, messages: AiMessage[]) => void;
  /** @deprecated 密钥由服务端管理。 */
  onApiKeyChange?: (apiKey: string) => void;
  /** @deprecated 模式由学习状态管理。 */
  onAiModeChange?: (mode: "online" | "local") => void;
  onOpenChange: (open: boolean) => void;
}

export const ASSISTANT_INTRO_DELAY_MS = 600;
export const ASSISTANT_PROMPT_DURATION_MS = 10_000;
const ASSISTANT_INTRO_TEXT = "你好！我是本次旅程的助学 AI 物候搭档。有疑问欢迎随时和我交流讨论！";
const ASSISTANT_HELP_TEXT = "这一步好像有点难，需要我和你一起找找线索吗？";

function createInitialMessage(chapterId: ChapterId | "quiz", sceneId: SceneId): AiMessage {
  return {
    id: `intro-${chapterId}`,
    role: "assistant",
    text: chapterId === "quiz"
      ? "我能看到你当前的题目、已选内容和最近一次操作。直接问我，我会结合课文说明答案与依据。"
      : "我能看到当前场景、操作进度、已查看证据和最近一次错误。直接问“这里怎么做”也可以。",
    sceneId,
    evidenceRefs: [],
    createdAt: Date.now()
  };
}

function createMessageId(role: AiMessage["role"]): string {
  return `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function BoldText({ value }: { value: string }) {
  return (
    <span className="ai-rich-text">
      {parseBoldMarkdown(value).map((segment, index) => segment.bold
        ? <strong key={`${index}-${segment.text}`}>{segment.text}</strong>
        : <span key={`${index}-${segment.text}`}>{segment.text}</span>)}
    </span>
  );
}

function initialThread(messages: readonly AiMessage[], chapterId: ChapterId | "quiz", sceneId: SceneId): AiMessage[] {
  return messages.length > 0 ? [...messages] : [createInitialMessage(chapterId, sceneId)];
}

export function responseFallbackReason(response: AiResponse): FallbackReason | undefined {
  return response.mode === "fallback" ? response.fallbackReason : undefined;
}

export function createAssistantMessage(response: AiResponse, sceneId: SceneId): AiMessage {
  const fallbackReason = responseFallbackReason(response);
  return {
    id: createMessageId("assistant"),
    role: "assistant",
    text: response.message,
    sceneId,
    evidenceRefs: response.evidenceRefs.filter((id): id is EvidenceId => /^P\d{2,3}$/u.test(id)),
    mode: response.mode,
    ...(fallbackReason ? { fallbackReason } : {}),
    createdAt: Date.now()
  };
}

const HINT_TERM_SOURCE = "(?:提示|线索|点拨|思路|答案方向|作答方向|解题方向)";
const HINT_NEGATION_PATTERN = new RegExp(
  `(?:不要|别|无需|不需要|不用)(?:再|直接)?(?:给我|给|提供)?(?:任何|一点|任何一点)?\\s*${HINT_TERM_SOURCE}`,
  "u"
);
const HINT_REQUEST_PATTERN = new RegExp(
  `(?:请|能|能否|能不能|可以|可否|麻烦|帮(?:我)?|给(?:我)?|告诉(?:我)?|提供|需要|想要|来点|求).{0,12}${HINT_TERM_SOURCE}|${HINT_TERM_SOURCE}(?:一下|一点|一些|吗|么|呢|吧|可以吗|好不好|[?？])$`,
  "u"
);

export function isExplicitHintRequest(value: string): boolean {
  const normalized = value.trim();
  return normalized.length > 0
    && !HINT_NEGATION_PATTERN.test(normalized)
    && HINT_REQUEST_PATTERN.test(normalized);
}

export function recentDialogue(messages: readonly AiMessage[], sceneId?: SceneId): AiDialogueMessage[] {
  const normalized = messages
    .filter((message) => !message.id.startsWith("intro-") && (!sceneId || message.sceneId === sceneId))
    .map((message) => ({
      role: message.role,
      content: message.text.trim().slice(0, message.role === "user" ? 120 : 180)
    }))
    .filter((message) => message.content.length > 0);

  const rounds: AiDialogueMessage[][] = [];
  for (let index = normalized.length - 1; index > 0 && rounds.length < 2;) {
    const assistant = normalized[index];
    const user = normalized[index - 1];
    if (user.role === "user" && assistant.role === "assistant") {
      rounds.unshift([user, assistant]);
      index -= 2;
    } else {
      index -= 1;
    }
  }
  return rounds.flat();
}

interface RequestContext {
  threadId: AiThreadId;
  snapshotIdentity: string;
  aiMode: "online" | "local";
  open: boolean;
}

export function isRequestContextCurrent(current: RequestContext, expected: Omit<RequestContext, "open">): boolean {
  return current.open
    && current.threadId === expected.threadId
    && current.snapshotIdentity === expected.snapshotIdentity
    && current.aiMode === expected.aiMode;
}

export function assistantRequestScope(snapshot: Pick<AssistantContextSnapshot, "sceneId">): SceneId {
  return snapshot.sceneId;
}

function messageAssetId(message: AiMessage): "1D-01" | "1D-04" | "1D-05" {
  if (message.mode === "fallback") return "1D-05";
  if (message.evidenceRefs.length > 0) return "1D-04";
  return "1D-01";
}

type AssistantRequestState = "idle" | "submitting" | "fallback" | "error" | "canceled";
type SettingsStatus = { kind: "success" | "error" | "info"; message: string };
type RequestNoticeTone = "danger" | "warning" | "info";

const fallbackNotice: Record<FallbackReason, string> = {
  "not-configured": "尚未配置在线密钥，已切换为本地引导。",
  "invalid-key": "API Key 无效或无权限，已切换为本地引导。",
  "rate-limited": "在线服务请求过于频繁，已切换为本地引导。",
  timeout: "在线服务响应超时，已切换为本地引导。",
  "invalid-context": "学习状态未能同步，已保留问题并切换为本地引导。",
  "model-unavailable": "当前在线模型配置不可用，已切换为本地引导。",
  "upstream-error": "在线服务暂时不可用，已切换为本地引导。",
  "invalid-response": "在线回复格式异常，已切换为本地引导。",
  "network-error": "无法连接本地 AI 服务，已切换为本地引导。"
};

const fallbackTitle: Record<FallbackReason, string> = {
  "not-configured": "尚未配置在线服务",
  "invalid-key": "密钥未通过验证",
  "rate-limited": "请求过于频繁",
  timeout: "在线响应超时",
  "invalid-context": "学习状态未同步",
  "model-unavailable": "在线模型配置不可用",
  "upstream-error": "在线服务暂不可用",
  "invalid-response": "在线回复格式异常",
  "network-error": "无法连接本地 AI 服务"
};

function noticeTone(state: AssistantRequestState, reason?: FallbackReason): RequestNoticeTone {
  if (state === "error") return "danger";
  if (state === "canceled") return "info";
  if (reason === "invalid-key" || reason === "invalid-context" || reason === "invalid-response" || reason === "network-error") return "danger";
  return "warning";
}

interface RetryExchange {
  question: string;
  userId: string;
  assistantId: string;
}

function usesCoarsePointer(): boolean {
  return window.matchMedia?.("(pointer: coarse)").matches ?? false;
}

// 学习助手保留章节内对话，但当前面板和上传上下文都只使用当前场景消息。
export function AiCompanion({
  open,
  threadId,
  chapterId,
  snapshot,
  aiMode,
  messages,
  introAcknowledged,
  taskEvent,
  onHintUsed,
  onIntroAcknowledged,
  onEvidence,
  onEvidenceViewed,
  onThreadChange,
  onOpenChange
}: AiCompanionProps) {
  const sceneId = snapshot.sceneId;
  const snapshotIdentity = assistantRequestScope(snapshot);
  const [question, setQuestion] = useState("");
  const [threadMessages, setThreadMessages] = useState<AiMessage[]>(() => initialThread(messages, chapterId, sceneId));
  const [requestState, setRequestState] = useState<AssistantRequestState>("idle");
  const [requestNotice, setRequestNotice] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [retryQuestion, setRetryQuestion] = useState<string | null>(null);
  const [promptState, dispatchPrompt] = useReducer(
    reduceAssistantPromptState,
    sceneId,
    createAssistantPromptState
  );
  const [promptTimerPaused, setPromptTimerPaused] = useState(false);
  const requestToken = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const pendingQuestionRef = useRef<string | null>(null);
  const retryExchange = useRef<RetryExchange | null>(null);
  const currentRequestContext = useRef({ threadId, snapshotIdentity, aiMode, open });
  currentRequestContext.current = { threadId, snapshotIdentity, aiMode, open };
  const initializedThread = useRef<AiThreadId | null>(null);
  const orbRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const lastHandledTaskEventId = useRef(taskEvent?.id ?? 0);
  const promptTimer = useRef<number | null>(null);
  const promptTimerKind = useRef<AssistantPromptKind | null>(null);
  const promptTimerRemaining = useRef(ASSISTANT_PROMPT_DURATION_MS);
  const promptTimerStartedAt = useRef<number | null>(null);
  const promptTimerWasPaused = useRef(false);
  const promptHovered = useRef(false);
  const promptFocused = useRef(false);
  const loading = requestState === "submitting";

  const visibleMessages = useMemo(
    () => threadMessages.filter((message) => message.sceneId === sceneId),
    [sceneId, threadMessages]
  );
  const introMessage = useMemo(() => createInitialMessage(chapterId, sceneId), [chapterId, sceneId]);
  const displayedMessages = visibleMessages.length > 0 ? visibleMessages : [introMessage];
  const lastAssistant = useMemo(
    () => [...visibleMessages].reverse().find((message) => message.role === "assistant"),
    [visibleMessages]
  );
  const retryableFallback = lastAssistant?.fallbackReason !== "invalid-key"
    && lastAssistant?.fallbackReason !== "not-configured";
  const modeLabel = aiMode === "local"
      ? "本地提示"
      : loading
        ? "在线思考"
        : requestState === "fallback"
          ? "本地回退"
          : "在线候答";
  const requestFallbackReason = requestState === "fallback" ? lastAssistant?.fallbackReason : undefined;
  const requestNoticeTone = noticeTone(requestState, requestFallbackReason);
  const requestNoticeTitle = requestState === "error"
    ? "请求未完成"
    : requestState === "canceled"
      ? "请求已取消"
      : requestFallbackReason
        ? fallbackTitle[requestFallbackReason]
        : "已切换为本地引导";
  const RequestNoticeIcon = requestNoticeTone === "danger"
    ? AlertCircle
    : requestNoticeTone === "warning"
      ? AlertTriangle
      : Info;
  const avatarId = question.trim()
    ? "1D-03"
    : loading
      ? "1D-02"
      : aiMode === "local" || lastAssistant?.mode === "fallback"
        ? "1D-05"
        : lastAssistant?.evidenceRefs.length
          ? "1D-04"
          : "1D-01";
  const visiblePrompt = promptState.sceneId === sceneId ? promptState.visiblePrompt : null;
  const promptText = visiblePrompt === "help"
    ? introAcknowledged
      ? ASSISTANT_HELP_TEXT
      : `我是助学 AI 物候搭档。${ASSISTANT_HELP_TEXT}`
    : ASSISTANT_INTRO_TEXT;

  useEffect(() => {
    dispatchPrompt({ type: "scene-change", sceneId });
  }, [sceneId]);

  useEffect(() => {
    if (!taskEvent || taskEvent.id === lastHandledTaskEventId.current) return;
    lastHandledTaskEventId.current = taskEvent.id;
    dispatchPrompt({ type: "task-event", event: taskEvent });
    if (open) dispatchPrompt({ type: "open-assistant" });
  }, [open, taskEvent]);

  useEffect(() => {
    if (open || introAcknowledged || visiblePrompt || promptState.sceneId !== sceneId) return;
    const timer = window.setTimeout(() => {
      dispatchPrompt({ type: "intro-ready" });
    }, ASSISTANT_INTRO_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [introAcknowledged, open, promptState.sceneId, sceneId, visiblePrompt]);

  useEffect(() => {
    if (open) dispatchPrompt({ type: "open-assistant" });
  }, [open]);

  useEffect(() => {
    if (introAcknowledged && visiblePrompt === "intro") {
      dispatchPrompt({ type: "dismiss-prompt" });
    }
  }, [introAcknowledged, visiblePrompt]);

  useEffect(() => {
    const now = Date.now();
    const promptChanged = promptTimerKind.current !== visiblePrompt;
    if (
      !promptChanged
      && !promptTimerWasPaused.current
      && promptTimerPaused
      && promptTimerStartedAt.current !== null
    ) {
      promptTimerRemaining.current = Math.max(
        0,
        promptTimerRemaining.current - (now - promptTimerStartedAt.current)
      );
    }

    if (promptTimer.current !== null) window.clearTimeout(promptTimer.current);
    promptTimer.current = null;
    promptTimerStartedAt.current = null;
    if (promptChanged) promptTimerRemaining.current = ASSISTANT_PROMPT_DURATION_MS;
    promptTimerKind.current = visiblePrompt;
    promptTimerWasPaused.current = promptTimerPaused;

    if (!visiblePrompt) {
      promptHovered.current = false;
      promptFocused.current = false;
      if (promptTimerPaused) setPromptTimerPaused(false);
      return;
    }
    if (promptTimerPaused) return;

    promptTimerStartedAt.current = now;
    promptTimer.current = window.setTimeout(() => {
      dispatchPrompt({ type: "auto-dismiss-prompt", prompt: visiblePrompt });
    }, promptTimerRemaining.current);
    return () => {
      if (promptTimer.current !== null) window.clearTimeout(promptTimer.current);
      promptTimer.current = null;
    };
  }, [promptTimerPaused, visiblePrompt]);

  useEffect(() => {
    if (initializedThread.current === threadId) return;
    initializedThread.current = threadId;
    setThreadMessages(initialThread(messages, chapterId, sceneId));
    setQuestion("");
    setRequestState("idle");
    setRequestNotice(null);
    setRetryQuestion(null);
    retryExchange.current = null;
  }, [chapterId, messages, sceneId, threadId]);

  const cancelActiveRequest = useCallback((notice?: string, restoreDraft = true): boolean => {
    if (!activeRequest.current) return false;
    requestToken.current += 1;
    activeRequest.current.abort();
    activeRequest.current = null;
    const pending = pendingQuestionRef.current;
    pendingQuestionRef.current = null;
    setPendingQuestion(null);
    if (restoreDraft && pending) setQuestion(pending);
    if (pending) setRetryQuestion(pending);
    setRequestState("canceled");
    setRequestNotice(notice ?? "请求已取消，问题已恢复，可再次发送。");
    return true;
  }, []);

  useEffect(() => {
    const canceled = cancelActiveRequest("当前场景或助手模式已变化，原问题已恢复。", true);
    retryExchange.current = null;
    setRetryQuestion(null);
    if (!canceled) {
      setRequestState("idle");
      setRequestNotice(null);
    }
  }, [aiMode, cancelActiveRequest, snapshotIdentity, threadId]);

  useEffect(() => {
    if (open) return;
    cancelActiveRequest(undefined, true);
  }, [cancelActiveRequest, open]);

  useEffect(() => () => {
    requestToken.current += 1;
    activeRequest.current?.abort();
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!usesCoarsePointer()) inputRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closePanel();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open || !messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [displayedMessages.length, loading, open]);

  function commit(next: AiMessage[]) {
    const capped = next.slice(-MAX_AI_THREAD_MESSAGES);
    setThreadMessages(capped);
    onThreadChange(threadId, capped);
  }

  function closePanel() {
    cancelActiveRequest(undefined, true);
    onOpenChange(false);
    orbRef.current?.focus();
    window.requestAnimationFrame?.(() => orbRef.current?.focus());
  }

  function acknowledgeIntro() {
    if (!introAcknowledged) onIntroAcknowledged();
  }

  function openPanel() {
    acknowledgeIntro();
    dispatchPrompt({ type: "open-assistant" });
    onOpenChange(true);
  }

  function dismissPrompt() {
    acknowledgeIntro();
    dispatchPrompt({ type: "dismiss-prompt" });
    window.requestAnimationFrame(() => orbRef.current?.focus());
  }

  function updatePromptTimerPause() {
    setPromptTimerPaused(promptHovered.current || promptFocused.current);
  }

  function handlePromptMouseEnter() {
    promptHovered.current = true;
    updatePromptTimerPause();
  }

  function handlePromptMouseLeave() {
    promptHovered.current = false;
    updatePromptTimerPause();
  }

  function handlePromptFocus() {
    promptFocused.current = true;
    updatePromptTimerPause();
  }

  function handlePromptBlur(event: React.FocusEvent<HTMLElement>) {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    promptFocused.current = false;
    updatePromptTimerPause();
  }

  async function submitQuestion(value = question, retry = false) {
    const trimmed = value.trim();
    if (!open || !trimmed || loading) return;
    if (!retry && isExplicitHintRequest(trimmed)) onHintUsed();

    const replaced = retry ? retryExchange.current : null;
    const baseMessages = replaced
      ? threadMessages.filter((message) => message.id !== replaced.userId && message.id !== replaced.assistantId)
      : threadMessages;
    setQuestion("");
    setPendingQuestion(trimmed);
    pendingQuestionRef.current = trimmed;
    setRequestState("submitting");
    setRequestNotice(null);

    const request = {
      chapterId,
      studentText: trimmed,
      snapshot: {
        ...snapshot,
        recentMessages: recentDialogue(baseMessages, sceneId)
      }
    };

    try {
      let response: AiResponse;
      if (aiMode === "local") {
        response = createLocalAiGuidance(request);
      } else {
        const token = ++requestToken.current;
        const controller = new AbortController();
        activeRequest.current?.abort();
        activeRequest.current = controller;
        response = await requestAiGuidance(request, { signal: controller.signal });
        const contextIsCurrent = isRequestContextCurrent(currentRequestContext.current, {
          threadId,
          snapshotIdentity,
          aiMode
        });
        if (token !== requestToken.current || !contextIsCurrent) return;
        activeRequest.current = null;
      }

      const userMessage: AiMessage = {
        id: createMessageId("user"),
        role: "user",
        text: trimmed,
        sceneId,
        evidenceRefs: [],
        createdAt: Date.now()
      };
      const assistantMessage = createAssistantMessage(response, sceneId);
      commit([...baseMessages, userMessage, assistantMessage]);
      pendingQuestionRef.current = null;
      setPendingQuestion(null);
      const fallbackReason = assistantMessage.fallbackReason;
      if (fallbackReason) {
        retryExchange.current = { question: trimmed, userId: userMessage.id, assistantId: assistantMessage.id };
        setRetryQuestion(trimmed);
        setRequestState("fallback");
        setRequestNotice(fallbackNotice[fallbackReason]);
      } else {
        retryExchange.current = null;
        setRetryQuestion(null);
        setRequestState("idle");
        setRequestNotice(null);
      }
    } catch (error) {
      if (error instanceof AiRequestAbortedError) return;
      activeRequest.current = null;
      pendingQuestionRef.current = null;
      setPendingQuestion(null);
      setQuestion(trimmed);
      setRetryQuestion(trimmed);
      setRequestState("error");
      setRequestNotice("请求未完成，问题已恢复。请检查服务状态后重试。");
    }
  }

  return (
    <>
      {!open && visiblePrompt && (
        <aside
          className={`ai-callout ai-callout--${visiblePrompt}`}
          data-prompt-kind={visiblePrompt}
          aria-label={visiblePrompt === "help" ? "物候搭档求助提示" : "物候搭档介绍"}
          onMouseEnter={handlePromptMouseEnter}
          onMouseLeave={handlePromptMouseLeave}
          onFocusCapture={handlePromptFocus}
          onBlurCapture={handlePromptBlur}
        >
          <p className="ai-callout__message" role="status" aria-live="polite" aria-atomic="true">{promptText}</p>
          <button
            className="ai-callout__close"
            type="button"
            onClick={dismissPrompt}
            title="关闭提示"
            aria-label={visiblePrompt === "help" ? "关闭物候搭档求助提示" : "关闭物候搭档介绍"}
          >
            <X size={16} />
          </button>
          {visiblePrompt === "help" && (
            <button className="ai-callout__action" type="button" onClick={openPanel}>
              <MessageCircle size={16} /> 问问物候搭档
            </button>
          )}
        </aside>
      )}
      <button
        ref={orbRef}
        className={`ai-orb ${open ? "is-open" : ""}`}
        type="button"
        onClick={() => open ? closePanel() : openPanel()}
        aria-label={open ? "关闭学习助手" : "打开学习助手"}
        title={open ? "关闭学习助手" : "打开学习助手"}
        aria-expanded={open}
        aria-controls="ai-companion-panel"
      >
        <AssetImage assetId={avatarId} className="ai-orb__avatar" alt="" sizes="58px" />
        <span className="ai-orb__badge" aria-hidden="true">AI</span>
      </button>

      {open ? (
        <aside id="ai-companion-panel" className="ai-panel" role="dialog" aria-modal="false" aria-label="学习助手">
          <header className="ai-panel__header">
            <div className="ai-panel__identity">
              <AssetImage assetId={avatarId} className="ai-panel__avatar" alt="物候学习助手" sizes="46px" />
              <div>
                <strong><Sparkles size={15} /> 物候搭档</strong>
                <small>{modeLabel}</small>
              </div>
            </div>
            <div className="ai-panel__tools">
              <button className="icon-button" type="button" onClick={closePanel} title="关闭学习助手" aria-label="关闭学习助手">
                <X size={18} />
              </button>
            </div>
          </header>

          <>
              {requestNotice && (
                <div
                  className={`ai-panel__status ai-panel__status--${requestState} ai-panel__status--${requestNoticeTone}`}
                  role={requestNoticeTone === "danger" ? "alert" : "status"}
                >
                  <RequestNoticeIcon className="ai-panel__status-icon" size={18} aria-hidden="true" />
                  <div className="ai-panel__status-copy"><strong>{requestNoticeTitle}</strong><span>{requestNotice}</span></div>
                  <div className="ai-panel__status-actions">
                    {retryQuestion && (
                      requestState === "error"
                      || requestState === "canceled"
                      || (requestState === "fallback" && retryableFallback)
                    ) && (
                      <button type="button" onClick={() => { void submitQuestion(retryQuestion, true); }} disabled={loading}>
                        <RotateCcw size={14} /> 重试
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="ai-panel__messages" ref={messagesRef} role="log">
                {displayedMessages.map((message) => (
                  <div className={`ai-message ai-message--${message.role}`} data-message-role={message.role} key={message.id}>
                    {message.role === "assistant" && <AssetImage assetId={messageAssetId(message)} className="ai-message__avatar" alt="" sizes="28px" />}
                    <div className="ai-message__body">
                      <div className="ai-message__bubble"><BoldText value={message.text} /></div>
                      {message.role === "assistant" && message.evidenceRefs.length > 0 && (
                        <div className="ai-message__meta">
                          <div className="ai-message__citations">
                            {message.evidenceRefs.map((paragraphId) => (
                              <button className="ai-citation" type="button" key={`${message.id}-${paragraphId}`} onClick={() => {
                                onEvidenceViewed(paragraphId, message.sceneId);
                                onEvidence(paragraphId, `AI 引用 · ${paragraphId}`);
                              }}>核验课文 {paragraphId}</button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {pendingQuestion && (
                  <div className="ai-message ai-message--user ai-message--pending" data-message-role="user" aria-hidden="true">
                    <div className="ai-message__bubble">{pendingQuestion}</div>
                  </div>
                )}
                {loading && (
                  <div className="ai-message ai-message--assistant ai-message--loading" data-message-role="assistant">
                    <AssetImage assetId="1D-02" className="ai-message__avatar" alt="" sizes="28px" />
                    <div className="ai-message__bubble" aria-label="学习助手正在思考">正在对照当前章节……</div>
                  </div>
                )}
              </div>
              <form className="ai-panel__composer" onSubmit={(event) => { event.preventDefault(); void submitQuestion(); }}>
                <label className="sr-only" htmlFor="ai-question">向学习助手提问</label>
                <input id="ai-question" ref={inputRef} value={question} onChange={(event) => setQuestion(event.target.value.slice(0, 300))} placeholder="写下你的疑惑……" maxLength={300} autoComplete="off" />
                <button className="ai-panel__send" type="submit" disabled={!question.trim() || loading} title="发送" aria-label="发送问题"><Send size={17} /></button>
              </form>
              <small className="ai-panel__notice">学习助手会结合当前操作与课文证据直接回答；完成状态仍以系统记录为准。</small>
          </>
        </aside>
      ) : null}
    </>
  );
}
